/**
 * Metric-tile measurements, mirrored from backend/utils/report-helpers.js.
 *
 * The backend is the source of truth; this file exists so the editor's preview
 * and config panel can resolve the same numbers without importing server code.
 * backend/scripts/verify-report-bands.js loads this module and compares it with
 * the backend's own resolver, so the two cannot drift.
 *
 * Keep it dependency-free: the harness evaluates the file as plain text with
 * `export ` stripped, so an import statement here is a syntax error there.
 */

/**
 * Points between a tile's caption and its figure.
 *
 * 4pt is what the PDF renderer has always drawn. Word drew 1pt and the preview
 * 2px, for no reason beyond three renderers each picking a literal -- which is
 * why this is a resolver now rather than three numbers.
 */
export const METRIC_TILE_LABEL_GAP = 4;

/** The tile's own top and bottom padding, in points. */
export const METRIC_TILE_PAD = 3;

/**
 * The caption-to-figure gap an author asked for, or the house default.
 *
 * Deliberately not `config.labelGap || DEFAULT`: an author who wants the
 * caption tight against the figure stores 0, and `||` would quietly turn that
 * back into 4 -- the same class of bug as a measurement with no control at all.
 */
export function metricTileLabelGap(config) {
  const raw = (config || {}).labelGap;
  // null and '' are "not set", and Number() turns both into 0 -- which would
  // read as an author asking for no gap at all.
  if (raw === null || raw === undefined || raw === '') return METRIC_TILE_LABEL_GAP;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return METRIC_TILE_LABEL_GAP;
  return Math.min(24, n);
}

/** The alignments a tile may carry, in the order the editor offers them. */
export const METRIC_TILE_ALIGNMENTS = ['left', 'center', 'right'];
export const METRIC_TILE_ALIGN = 'center';

/**
 * How one tile's caption and figure are aligned: the tile's own setting, then
 * the band's, then centre.
 *
 * An unrecognised value falls THROUGH to the next level rather than being
 * passed on, because Word's alignFromStr maps anything it does not know to
 * left -- a stray value would silently left-align one tile in a centred strip.
 * The empty string is how this panel says "use the band's setting".
 */
export function metricTileAlign(tile, config) {
  const pick = (v) => (typeof v === 'string' && METRIC_TILE_ALIGNMENTS.includes(v.toLowerCase())
    ? v.toLowerCase() : null);
  return pick((tile || {}).align) || pick((config || {}).tileAlign) || METRIC_TILE_ALIGN;
}
